import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UsersIcon,
  CurrencyDollarIcon,
  TrophyIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { batchesAPI, membersAPI, drawsAPI, settingsAPI } from '../services/api';
import { LoadingSpinner, StatCard, Badge, EmptyState, DataTable } from '../components/common';
import toast from 'react-hot-toast';
import { formatCurrency, formatDate, formatNumber } from '../utils/formatters';

export default function BatchDetailPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const { data: batch, isLoading } = useQuery({
    queryKey: ['batch', id],
    queryFn: async () => {
      const response = await batchesAPI.getById(id);
      return response.data;
    },
  });

  const { data: liveStats } = useQuery({
    queryKey: ['batch-live-stats', id],
    queryFn: async () => {
      const response = await batchesAPI.getLiveStats(id);
      return response.data;
    },
    enabled: !!id,
  });

  const { data: batchSettingsData, isLoading: isBatchSettingsLoading } = useQuery({
    queryKey: ['batch-settings', id],
    queryFn: async () => {
      const response = await settingsAPI.getBatch(id);
      return response.data;
    },
    enabled: !!id,
  });

  const [batchSettingsForm, setBatchSettingsForm] = useState(null);
  const { data: globalSettingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await settingsAPI.get();
      return response.data?.settings || response.data;
    },
  });

  useEffect(() => {
    if (batchSettingsData?.settings) {
      setBatchSettingsForm(batchSettingsData.settings);
    }
  }, [batchSettingsData]);

  const updateBatchSettingsMutation = useMutation({
    mutationFn: (data) => settingsAPI.updateBatch(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['batch-settings', id]);
      toast.success('Batch settings updated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update batch settings');
    },
  });
  
  const { data: paymentStatus } = useQuery({
    queryKey: ['batch-payment-status', id],
    queryFn: async () => {
      const response = await batchesAPI.getPaymentStatus(id);
      return response.data;
    },
    enabled: !!id,
  });

  const { data: members } = useQuery({
    queryKey: ['batch-members', id],
    queryFn: async () => {
      const response = await membersAPI.getBatchMembers(id);
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    },
    enabled: !!id,
  });

  const { data: draws } = useQuery({
    queryKey: ['batch-draws', id],
    queryFn: async () => {
      const response = await drawsAPI.getBatchDraws(id);
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    },
    enabled: !!id,
  });

  if (isLoading) {
    return <LoadingSpinner text="Loading batch details..." />;
  }

  const batchData = batch?.data;
  const statsData = liveStats?.data;
  const paymentData = paymentStatus?.data;

  console.log(statsData);


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/batches" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">
              {batchData?.name || `Batch ${batchData?.batch_number}`}
            </h1>
            <Badge variant={
              batchData?.status === 'active' ? 'success' :
              batchData?.status === 'open' ? 'info' :
              batchData?.status === 'full' ? 'warning' :
              'default'
            }>
              {batchData?.status}
            </Badge>
          </div>

      {/* Batch Settings (per-batch overrides) */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Batch Settings</h2>
          {isBatchSettingsLoading && <span className="text-xs text-gray-500">Loading...</span>}
        </div>
        {batchSettingsForm ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                <span>Winning Amount (cents)</span>
                {globalSettingsData && batchSettingsForm.winning_amount_cents !== undefined &&
                  batchSettingsForm.winning_amount_cents !== globalSettingsData.winning_amount_cents && (
                    <span className="text-xs text-primary-600 font-medium">Overridden</span>
                  )}
              </label>
              <input
                type="number"
                className="input-field"
                value={batchSettingsForm.winning_amount_cents ?? ''}
                onChange={(e) =>
                  setBatchSettingsForm((prev) => ({
                    ...prev,
                    winning_amount_cents: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                <span>Non-winner Daily Deposit (cents)</span>
                {globalSettingsData && batchSettingsForm.non_winner_daily_deposit_cents !== undefined &&
                  batchSettingsForm.non_winner_daily_deposit_cents !== globalSettingsData.non_winner_daily_deposit_cents && (
                    <span className="text-xs text-primary-600 font-medium">Overridden</span>
                  )}
              </label>
              <input
                type="number"
                className="input-field"
                value={batchSettingsForm.non_winner_daily_deposit_cents ?? ''}
                onChange={(e) =>
                  setBatchSettingsForm((prev) => ({
                    ...prev,
                    non_winner_daily_deposit_cents: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                <span>Winner Daily Deposit (cents)</span>
                {globalSettingsData && batchSettingsForm.winner_daily_deposit_cents !== undefined &&
                  batchSettingsForm.winner_daily_deposit_cents !== globalSettingsData.winner_daily_deposit_cents && (
                    <span className="text-xs text-primary-600 font-medium">Overridden</span>
                  )}
              </label>
              <input
                type="number"
                className="input-field"
                value={batchSettingsForm.winner_daily_deposit_cents ?? ''}
                onChange={(e) =>
                  setBatchSettingsForm((prev) => ({
                    ...prev,
                    winner_daily_deposit_cents: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                <span>Daily Number of Winners</span>
                {globalSettingsData && batchSettingsForm.daily_number_of_winners !== undefined &&
                  batchSettingsForm.daily_number_of_winners !== globalSettingsData.daily_number_of_winners && (
                    <span className="text-xs text-primary-600 font-medium">Overridden</span>
                  )}
              </label>
              <input
                type="number"
                className="input-field"
                value={batchSettingsForm.daily_number_of_winners ?? ''}
                onChange={(e) =>
                  setBatchSettingsForm((prev) => ({
                    ...prev,
                    daily_number_of_winners: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                <span>Daily Number of Fixed Winners</span>
                {globalSettingsData && batchSettingsForm.daily_number_of_fixed_winners !== undefined &&
                  batchSettingsForm.daily_number_of_fixed_winners !== globalSettingsData.daily_number_of_fixed_winners && (
                    <span className="text-xs text-primary-600 font-medium">Overridden</span>
                  )}
              </label>
              <input
                type="number"
                className="input-field"
                value={batchSettingsForm.daily_number_of_fixed_winners ?? ''}
                onChange={(e) =>
                  setBatchSettingsForm((prev) => ({
                    ...prev,
                    daily_number_of_fixed_winners: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                <span>Service Fee (cents)</span>
                {globalSettingsData && batchSettingsForm.service_fee_cents !== undefined &&
                  batchSettingsForm.service_fee_cents !== globalSettingsData.service_fee_cents && (
                    <span className="text-xs text-primary-600 font-medium">Overridden</span>
                  )}
              </label>
              <input
                type="number"
                className="input-field"
                value={batchSettingsForm.service_fee_cents ?? ''}
                onChange={(e) =>
                  setBatchSettingsForm((prev) => ({
                    ...prev,
                    service_fee_cents: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                <span>Penalty Fee (cents)</span>
                {globalSettingsData && batchSettingsForm.penalty_fee_cents !== undefined &&
                  batchSettingsForm.penalty_fee_cents !== globalSettingsData.penalty_fee_cents && (
                    <span className="text-xs text-primary-600 font-medium">Overridden</span>
                  )}
              </label>
              <input
                type="number"
                className="input-field"
                value={batchSettingsForm.penalty_fee_cents ?? ''}
                onChange={(e) =>
                  setBatchSettingsForm((prev) => ({
                    ...prev,
                    penalty_fee_cents: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                <span>Drawer Response Seconds</span>
                {globalSettingsData && batchSettingsForm.drawer_response_seconds !== undefined &&
                  batchSettingsForm.drawer_response_seconds !== globalSettingsData.drawer_response_seconds && (
                    <span className="text-xs text-primary-600 font-medium">Overridden</span>
                  )}
              </label>
              <input
                type="number"
                className="input-field"
                value={batchSettingsForm.drawer_response_seconds ?? ''}
                onChange={(e) =>
                  setBatchSettingsForm((prev) => ({
                    ...prev,
                    drawer_response_seconds: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                <span>Max Draw Retry Drawers</span>
                {globalSettingsData && batchSettingsForm.max_draw_retry_drawers !== undefined &&
                  batchSettingsForm.max_draw_retry_drawers !== globalSettingsData.max_draw_retry_drawers && (
                    <span className="text-xs text-primary-600 font-medium">Overridden</span>
                  )}
              </label>
              <input
                type="number"
                className="input-field"
                value={batchSettingsForm.max_draw_retry_drawers ?? ''}
                onChange={(e) =>
                  setBatchSettingsForm((prev) => ({
                    ...prev,
                    max_draw_retry_drawers: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                  }))
                }
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No batch-specific settings loaded.</p>
        )}
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="btn-primary"
            disabled={!batchSettingsForm || updateBatchSettingsMutation.isLoading}
            onClick={() => updateBatchSettingsMutation.mutate(batchSettingsForm)}
          >
            {updateBatchSettingsMutation.isLoading ? 'Saving...' : 'Save Batch Settings'}
          </button>
        </div>
      </div>
          <p className="mt-1 text-sm text-gray-600">
            Batch #{batchData?.batch_number} • Created {formatDate(batchData?.created_at)}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Members"
          value={formatNumber(batchData?.current_members || 0)}
          subtitle={`${formatNumber(batchData?.total_slots || 0)} slots`}
          icon={UsersIcon}
          color="primary"
        />
        <StatCard
          title="Total Deposits"
          value={formatCurrency(statsData?.total_deposits_cents || 0)}
          icon={CurrencyDollarIcon}
          color="green"
        />
        <StatCard
          title="Days Completed"
          value={batchData?.days_completed || 0}
          subtitle={`${batchData?.total_days || 0} total days`}
          icon={ChartBarIcon}
          color="blue"
        />
        <StatCard
          title="Total Draws"
          value={statsData?.total_draws || 0}
          subtitle={`${statsData?.completed_draws || 0} completed`}
          icon={TrophyIcon}
          color="yellow"
        />
      </div>

      {/* Progress */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Enrollment Progress</h2>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Members Enrolled</span>
              <span className="font-semibold">
                {formatNumber(batchData?.current_members || 0)} / {formatNumber(batchData?.total_slots || 0)}
                ({Math.round(((batchData?.current_members || 0) / (batchData?.total_slots || 1)) * 100)}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-primary-600 h-3 rounded-full transition-all"
                style={{
                  width: `${Math.min(((batchData?.current_members || 0) / (batchData?.total_slots || 1)) * 100, 100)}%`
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Payment Status Breakdown */}
      {paymentData && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Status</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{statsData?.members?.green || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Green</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-3xl font-bold text-yellow-600">{statsData?.members?.yellow || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Yellow</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-3xl font-bold text-red-600">{statsData?.members?.red || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Red</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-600">{statsData?.members?.defaulted || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Defaulted</p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Members */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent Members</h2>
          <Link to={`/members?batch=${id}`} className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            View all →
          </Link>
        </div>
        {members && members.length > 0 ? (
          <div className="space-y-2">
            {members.slice(0, 5).map((member) => (
              <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{member.phone || `Member #${member.user_id}`}</p>
                  <p className="text-sm text-gray-500">
                    Enrolled {formatDate(member.enrolled_at)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    {formatCurrency(member.deposit_cents || 0)}
                  </p>
                  <Badge variant={
                    member.payment_status === 'green' ? 'success' :
                    member.payment_status === 'yellow' ? 'warning' :
                    'danger'
                  } size="sm">
                    {member.payment_status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={UsersIcon}
            title="No members yet"
            description="Members will appear here once they enroll"
          />
        )}
      </div>

      {/* Draw History */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Draw History</h2>
          <Link to="/draws" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            View all →
          </Link>
        </div>
        {draws && draws.length > 0 ? (
          <div className="space-y-2">
            {draws.slice(0, 5).map((draw) => (
              <div key={draw.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Draw #{draw.id}</p>
                  <p className="text-sm text-gray-500">
                    {formatDate(draw.draw_date)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">
                    {draw.total_winners || 0} winners
                  </p>
                  <Badge variant={
                    draw.status === 'completed' ? 'success' :
                    draw.status === 'in_progress' ? 'info' :
                    'default'
                  } size="sm">
                    {draw.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={TrophyIcon}
            title="No draws yet"
            description="Draw history will appear here"
          />
        )}
      </div>
    </div>
  );
}
